import { useUserAuth } from "@/context/UserAuthContext";
import { Heart, LogIn, LogOut, Shield } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Google sign-in / profile pill for the header.
 */
export default function GoogleAuthButton({ compact = false }) {
  const { user, loading, loginWithGoogle, logout } = useUserAuth();

  if (loading) return null;

  if (!user) {
    return (
      <button
        type="button"
        onClick={loginWithGoogle}
        data-testid="google-signin-btn"
        className={`inline-flex items-center gap-2 rounded-full border-2 border-ink/15 text-ink font-bold hover:border-palm hover:text-palm transition ${
          compact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"
        }`}
      >
        <GoogleG />
        <span>{compact ? "Sign in" : "Sign in with Google"}</span>
      </button>
    );
  }

  const first = user.name?.split(" ")[0] || "Friend";

  return (
    <div className="relative group" data-testid="user-pill">
      <button
        type="button"
        className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 bg-white px-2 py-1 text-sm font-semibold hover:border-palm transition"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
        ) : (
          <span className="w-7 h-7 rounded-full bg-palm text-white inline-flex items-center justify-center text-xs font-bold">{first[0]}</span>
        )}
        <span className="hidden md:inline">{first}</span>
        {user.role === "admin" && <Shield size={13} className="text-mango-deep" />}
      </button>
      <div className="absolute right-0 mt-2 bg-white border border-ink/10 rounded-[12px] shadow-xl p-2 min-w-[180px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50" data-testid="user-pill-menu">
        <div className="px-3 py-2 text-xs text-ink/60 border-b border-ink/10 truncate">{user.email}</div>
        <Link to="/my-programs" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-ink/5 rounded-md no-underline text-ink" data-testid="user-pill-my-programs">
          <Heart size={14} /> My programs
        </Link>
        {user.role === "admin" && (
          <Link to="/admin/dashboard" className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-ink/5 rounded-md no-underline text-ink" data-testid="user-pill-admin">
            <Shield size={14} /> Admin dashboard
          </Link>
        )}
        <button
          onClick={logout}
          data-testid="user-pill-logout"
          className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-berry/10 rounded-md text-berry"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.4 2.4 30 0 24 0 14.7 0 6.8 5.4 2.7 13.3l7.8 6c2-6 7.5-9.8 13.5-9.8z"/>
      <path fill="#4285F4" d="M46.9 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.9c-.6 3-2.3 5.6-5 7.3l7.7 6c4.5-4.1 7.3-10.3 7.3-17.8z"/>
      <path fill="#FBBC05" d="M10.5 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.8-6C.9 16.5 0 20.1 0 24s.9 7.5 2.7 10.7l7.8-6z"/>
      <path fill="#34A853" d="M24 48c6 0 11.2-2 15-5.4l-7.7-6c-2.1 1.4-4.9 2.3-7.3 2.3-6 0-11.5-3.8-13.5-9.8l-7.8 6C6.8 42.6 14.7 48 24 48z"/>
    </svg>
  );
}
